import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Fish, MessageSquare, Store, User, LogOut, Plus, Shield, ShieldAlert } from "lucide-react";

export const Header = () => {
  const { user, logout, isFirearmsPassActive } = useAuth();
  const navigate = useNavigate();

  const navClass = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition-colors duration-200 ${
      isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
    }`;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-50 border-b border-border backdrop-blur-xl bg-background/80"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16 gap-8">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <Fish className="w-5 h-5 text-primary-foreground" strokeWidth={2.2} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-base text-foreground">Florida Sportsman</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Community · Marketplace</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7 ml-4">
          <NavLink to="/forum" className={navClass} data-testid="nav-forum">Forum</NavLink>
          <NavLink to="/marketplace" className={navClass} data-testid="nav-marketplace">Marketplace</NavLink>
          {user && <NavLink to="/messages" className={navClass} data-testid="nav-messages">Messages</NavLink>}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {user ? (
            <>
              {isFirearmsPassActive && (
                <span
                  className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded border border-secondary/40 bg-secondary/10 text-secondary"
                  data-testid="badge-firearms-pass"
                  title="Firearms Pass active"
                >
                  <Shield className="w-3 h-3" /> 2A Pass
                </span>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate("/listings/new")}
                data-testid="btn-new-listing-header"
                className="hidden sm:inline-flex"
              >
                <Plus className="w-4 h-4 mr-1" /> Sell Gear
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full ring-2 ring-transparent hover:ring-accent transition" data-testid="btn-user-menu">
                    <Avatar className="w-9 h-9">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback>{user.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-card">
                  <DropdownMenuLabel>
                    <div className="font-semibold">{user.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/profile/${user.user_id}`)} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages">
                    <MessageSquare className="w-4 h-4 mr-2" /> Messages
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/marketplace")} data-testid="menu-marketplace">
                    <Store className="w-4 h-4 mr-2" /> Browse Marketplace
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/subscribe")} data-testid="menu-subscribe">
                    <Shield className="w-4 h-4 mr-2" /> {isFirearmsPassActive ? "Manage Firearms Pass" : "Get Firearms Pass"}
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate("/admin/reports")} data-testid="menu-admin">
                      <ShieldAlert className="w-4 h-4 mr-2" /> Admin · Reports
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button onClick={() => navigate("/login")} data-testid="btn-signin-header" className="bg-primary hover:bg-primary/90">
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
